import createReducer from '../../../../../utils/createReducer';
import { RootState, ThunkAction } from '~/store/types';
import { ACTION_PREFIX } from '../../sportsbook-constants';
import selectSportsbookState from '../../sportsbook-state-selector';
import {
	createSubscriptionToBets,
	disposeSubscriptionToBets
} from '~/components/sportsbook/lobby/api/betting-service-api';
import { BetSettlementStatus, BetStatus } from '../../sportsbook-types';
import { Bet, BetsResponse } from '../../api/betting-service/betting-service-types';
import { selectSportsbookUnifiedFeedEnabled } from '~/components/brand/ducks/selectors';
import normalizeUnifiedId from '../../utils/normalize-unified-id';

type State = {
	openBets: Array<Bet>;
	isLoading: boolean;
	subscriberRefs: Array<string>;
};

// Constants
const OPEN_BETS_REFERENCE = 'openBets';

// Actions
const OPEN_BETS_UPDATE_RECEIVED = `${ACTION_PREFIX}OPEN_BETS_UPDATE_RECEIVED` as const;
type OpenBetsUpdateReceivedAction = {
	type: typeof OPEN_BETS_UPDATE_RECEIVED;
	payload: {
		openBets: Array<Bet>;
	};
};

const OPEN_BETS_CASHOUT_BET_REMOVED = `${ACTION_PREFIX}OPEN_BETS_CASHOUT_BET_REMOVED` as const;
type OpenBetsCashoutBetRemovedAction = {
	type: typeof OPEN_BETS_CASHOUT_BET_REMOVED;
	payload: {
		betId: number;
	};
};

const SUBSCRIBED = `${ACTION_PREFIX}OPEN_BETS/SUBSCRIBED` as const;
type SubscribedAction = {
	type: typeof SUBSCRIBED;
	payload: {
		ref: string;
	};
};

const UNSUBSCRIBED = `${ACTION_PREFIX}OPEN_BETS/UNSUBSCRIBED` as const;
type UnsubscribedAction = {
	type: typeof UNSUBSCRIBED;
	payload: {
		ref: string;
	};
};

const DISPOSED = `${ACTION_PREFIX}OPEN_BETS/DISPOSED` as const;
type DisposedAction = {
	type: typeof DISPOSED;
};

// Selectors
const selectState = (state: RootState) => selectSportsbookState(state).openBets;
export const selectIsLoading = (state: RootState) => selectState(state).isLoading;
export const selectBets = (state: RootState) => selectState(state).openBets;
export const selectOpenBetsCount = (state: RootState) => selectBets(state).length;
const selectSubscriberRefs = (state: RootState) => selectState(state).subscriberRefs;
export const selectOpenBetsEventIds = (state: RootState) =>
	selectBets(state).flatMap(bet => bet.items.map(item => item.eventId));

const normalizeUnifiedIds = (data: BetsResponse, isUnifiedFeedEnabled: boolean): BetsResponse => {
	if (!normalizeUnifiedId || !data || !data.items) {
		return data;
	}

	return {
		...data,
		items: data.items.map(bet => ({
			...bet,
			items:
				bet.items?.map(item => ({
					...item,
					eventId: isUnifiedFeedEnabled ? normalizeUnifiedId(item.eventId) : item.eventId,
					marketId: isUnifiedFeedEnabled ? normalizeUnifiedId(item.marketId) : item.marketId,
					selectionId: isUnifiedFeedEnabled ? normalizeUnifiedId(item.selectionId) : item.selectionId
				})) || bet.items
		}))
	};
};

// Action creators
const subscribeToOpenBets = (): ThunkAction => (dispatch, getState) => {
	const isUnifiedFeedEnabled = selectSportsbookUnifiedFeedEnabled(getState());

	disposeSubscriptionToBets(OPEN_BETS_REFERENCE);

	createSubscriptionToBets(
		OPEN_BETS_REFERENCE,
		data => {
			dispatch({
				type: OPEN_BETS_UPDATE_RECEIVED,
				payload: {
					openBets: normalizeUnifiedIds(data, isUnifiedFeedEnabled).items
				}
			});
		},
		// eslint-disable-next-line no-console
		error => console.error(error)
	);
};

export const removeCashoutBet = (betId: number): OpenBetsCashoutBetRemovedAction => ({
	type: OPEN_BETS_CASHOUT_BET_REMOVED,
	payload: { betId }
});

/**
 * Initializes a subscription if required
 */
export const initialize =
	(ref: string): ThunkAction<SubscribedAction | SubscribedAction> =>
	(dispatch, getState) => {
		// Add ref to list of subscribers
		dispatch({ type: SUBSCRIBED, payload: { ref } });
		// Subscribe is none is subscribing
		const subscriberRefs = selectSubscriberRefs(getState());
		if (subscriberRefs.every(existingRef => existingRef === ref)) {
			dispatch(subscribeToOpenBets());
		}
	};

/**
 * Disposes state and subscription of open bets if no ref remains
 */
export const dispose =
	(ref: string): ThunkAction<DisposedAction | UnsubscribedAction> =>
	(dispatch, getState) => {
		// Remove ref from list of subscribers, this also recklessly throws away all non-open bets.
		dispatch({ type: UNSUBSCRIBED, payload: { ref } });
		// Unsubscribe if nobody is subscribing
		const subscriberRefs = selectSubscriberRefs(getState());
		if (subscriberRefs.length === 0) {
			disposeSubscriptionToBets(OPEN_BETS_UPDATE_RECEIVED);
			dispatch({ type: DISPOSED });
		}
	};

// Reducer
const initialState: State = {
	isLoading: true,
	openBets: [],
	subscriberRefs: []
};

const handleBetsUpdateReceived = (
	state: State,
	{ payload: { openBets: updatedBets } }: OpenBetsUpdateReceivedAction
): State => {
	let nextBets = [...state.openBets];

	// Add or update
	updatedBets.forEach(updatedBet => {
		const betIndex = nextBets.findIndex(existingBet => existingBet.id === updatedBet.id);
		if (betIndex !== -1) {
			nextBets[betIndex] = updatedBet;
			return;
		}
		nextBets.push(updatedBet);
	});

	// Filter and sort
	nextBets = nextBets
		.filter(
			bet =>
				bet.settlementStatus === BetSettlementStatus.OPEN ||
				bet.settlementStatus === BetSettlementStatus.CASHOUT
		)
		.sort((bet1, bet2) => new Date(bet2.placementDate).valueOf() - new Date(bet1.placementDate).valueOf());

	return {
		...state,
		isLoading: false,
		openBets: nextBets
	};
};

const handleCashoutBetRemoved = (state: State, { payload: { betId } }: OpenBetsCashoutBetRemovedAction): State => ({
	...state,
	openBets: state.openBets.filter(bet => bet.id !== betId)
});

const handleSubscribed = (state: State, { payload: { ref } }: SubscribedAction): State => ({
	...state,
	subscriberRefs: [...state.subscriberRefs, ref]
});

const handleUnsubscribed = (state: State, { payload: { ref } }: UnsubscribedAction): State => ({
	...state,
	subscriberRefs: state.subscriberRefs.filter(existingRef => existingRef !== ref),
	openBets: state.openBets.filter(bet => bet.status === BetStatus.UNSETTLED)
});

const handleDisposed = (): State => initialState;

export default createReducer(initialState, {
	[OPEN_BETS_UPDATE_RECEIVED]: handleBetsUpdateReceived,
	[OPEN_BETS_CASHOUT_BET_REMOVED]: handleCashoutBetRemoved,
	[SUBSCRIBED]: handleSubscribed,
	[UNSUBSCRIBED]: handleUnsubscribed,
	[DISPOSED]: handleDisposed
});
